/**
 * overlapResolution.js - Utilities for resolving meal scheduling conflicts
 * 
 * This module provides pure functions for handling meal overlaps when
 * rescheduling or repeating meals in the meal plan editor.
 */

/**
 * Available overlap resolution actions
 */
export const OverlapActions = {
    SWAP: 'swap',
    REPLACE: 'replace', 
    COMBINE: 'combine'
};

/**
 * Action configuration with labels and mode restrictions
 */
const OverlapActionConfig = {
    [OverlapActions.SWAP]: {
        label: 'SWAP with',
        modes: ['reschedule'] // Only available in reschedule mode
    },
    [OverlapActions.REPLACE]: {
        label: 'REPLACE with',
        modes: ['reschedule', 'repeat'] // Available in all modes
    },
    [OverlapActions.COMBINE]: {
        label: 'COMBINE with',
        modes: ['reschedule', 'repeat'] // Available in all modes
    }
};

/**
 * Sort overlaps by offset (day) and then by meal type
 * @param {Array} overlaps - Array of overlap objects
 * @param {Array} mealTypes - Array of meal type objects with name property
 * @returns {Array} Sorted overlaps
 */
export function sortOverlaps(overlaps, mealTypes = []) {
    // Create meal type order from the provided array
    const mealTypeOrder = mealTypes.map(mt => mt.name || mt);
    
    // Fallback to default order if no meal types provided
    const order = mealTypeOrder.length > 0 
        ? mealTypeOrder 
        : ['Breakfast', 'Lunch', 'Snack', 'Dinner'];
    
    return [...overlaps].sort((a, b) => {
        // First sort by day (offset)
        if (a.offset !== b.offset) {
            return a.offset - b.offset;
        }
        
        // Then sort by meal type
        const aIndex = order.indexOf(a.mealType);
        const bIndex = order.indexOf(b.mealType);
        
        // Handle meal types not in the order array
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        
        return aIndex - bIndex;
    });
}

/**
 * Process overlap resolutions and prepare meal updates
 * @param {Array} overlaps - Array of overlaps with their chosen actions
 * @param {Object} options - Additional options
 * @returns {Object} Object containing meals to update and delete
 */
export function resolveOverlaps(overlaps, options = {}) {
    const {
        mode = 'reschedule',
        additionalMeals = [],
        targetMealType = null,
        targetOffset = null,
        existingClears = []
    } = options;

    const mealsToUpdate = [];
    const mealsToDelete = [];
    const leftoverOffsets = [...existingClears];
    const processedMealIds = new Set();
    
    // Process each overlap based on chosen action
    overlaps.forEach((overlap) => {
        const { action, destMeals, sourceMeals, mealType, offset } = overlap;
        
        switch (action) {
            case OverlapActions.SWAP:
                // In swap mode, destination meals take source position
                destMeals.forEach(meal => {
                    const mealId = meal.uuid || meal.id;
                    if (!processedMealIds.has(mealId)) {
                        mealsToUpdate.push({
                            uuid: mealId,
                            updates: {
                                meal: sourceMeals[0].meal,
                                offset: sourceMeals[0].offset
                            }
                        });
                        processedMealIds.add(mealId);
                    }
                });
                break;
                
            case OverlapActions.REPLACE:
                // In replace mode, destination meals are deleted
                destMeals.forEach(meal => {
                    const mealId = meal.uuid || meal.id;
                    if (!processedMealIds.has(mealId)) {
                        mealsToDelete.push(meal);
                        processedMealIds.add(mealId);
                    }
                });
                break;
                
            case OverlapActions.COMBINE:
                // In combine mode, destination meals stay unchanged
                // Only source meals are moved
                break;
        }
        
        // Process source meals (handle leftovers like consumer does)
        sourceMeals.forEach(meal => {
            if (mode === 'reschedule' && meal.meal_type === 'leftover') {
                // Handle leftover meals specially - update their position (mirrors consumer logic)
                const mealId = meal.uuid || meal.id;
                if (!processedMealIds.has(mealId)) {
                    mealsToUpdate.push({
                        uuid: mealId,
                        updates: {
                            meal: mealType,
                            offset: offset
                        }
                    });
                    processedMealIds.add(mealId);
                }
                return;
            }
            
            const mealId = meal.uuid || meal.id;
            if (!processedMealIds.has(mealId)) {
                mealsToUpdate.push({
                    uuid: mealId,
                    updates: {
                        meal: mealType,
                        offset: offset
                    }
                });
                processedMealIds.add(mealId);
            }
        });
        
        // Add target offset to leftover offsets
        if (mode === 'reschedule' && !leftoverOffsets.includes(offset)) {
            leftoverOffsets.push(offset);
        }
    });

    existingClears.forEach((clear) => {
        const { sourceMeals, mealType, offset } = clear;

        // Process source meals (handle leftovers like consumer does)
        sourceMeals.forEach(meal => {
            const mealId = meal.uuid || meal.id;
            if (!processedMealIds.has(mealId)) {
                mealsToUpdate.push({
                    uuid: mealId,
                    updates: {
                        meal: mealType,
                        offset: offset
                    }
                });
                processedMealIds.add(mealId);
            }
        });
    })
    
    // Process additional meals (from the original selection)
    if (additionalMeals.length > 0 && targetMealType && targetOffset !== null) {
        additionalMeals.forEach(meal => {
            const mealId = meal.uuid || meal.id;
            if (!processedMealIds.has(mealId)) {
                mealsToUpdate.push({
                    uuid: mealId,
                    updates: {
                        meal: targetMealType,
                        offset: targetOffset
                    }
                });
                processedMealIds.add(mealId);
            }
        });
    }
    
    return {
        mealsToUpdate,
        mealsToDelete,
        leftoverOffsets: mode === 'reschedule' ? leftoverOffsets : undefined
    };
}

/**
 * Validate that all overlaps have valid actions
 * @param {Array} overlaps - Array of overlaps with actions
 * @returns {boolean} True if all overlaps have valid actions
 */
export function validateOverlapActions(overlaps) {
    if (!overlaps || overlaps.length === 0) return false;
    
    const validActions = Object.values(OverlapActions);
    return overlaps.every(overlap => 
        overlap.action && validActions.includes(overlap.action)
    );
}

/**
 * Get available actions for a given mode
 * @param {string} mode - 'reschedule' or 'repeat'
 * @param {boolean} returnMode - Whether we're in return mode (excludes swap)
 * @param {boolean} isRecipeSearch - Whether we're creating leftovers from recipe search (excludes swap)
 * @returns {Array} Array of action options
 */
export function getAvailableActions(mode, returnMode = false, isRecipeSearch = false) {
    return Object.entries(OverlapActionConfig)
        .filter(([action, config]) => {
            // Filter by mode availability
            if (!config.modes.includes(mode)) return false;

            // Filter out swap in return mode or recipe search mode (leftovers not created yet)
            if ((returnMode || isRecipeSearch) && action === OverlapActions.SWAP) return false;

            return true;
        })
        .map(([action, config]) => ({
            value: action,
            label: config.label
        }))
        .sort((a, b) => {
            // Ensure consistent ordering: SWAP, REPLACE, COMBINE
            const order = [OverlapActions.SWAP, OverlapActions.REPLACE, OverlapActions.COMBINE];
            return order.indexOf(a.value) - order.indexOf(b.value);
        });
}

/**
 * Initialize overlaps with default actions
 * @param {Array} overlaps - Raw overlap data
 * @param {string} defaultAction - Default action to apply
 * @returns {Array} Overlaps with actions initialized
 */
export function initializeOverlapActions(overlaps, defaultAction = OverlapActions.REPLACE) {
    return overlaps.map(overlap => ({
        ...overlap,
        action: overlap.action || defaultAction
    }));
}